import { FormInputDate } from '@/components/forms/form-input-date.js';
import { FormModal, type SubmitProps } from '@/components/form-modal.js';
import { Button } from '@/components/ui/button.js';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group.js';
import {
  bulkRemoveStudentGroupOverrideDates,
  bulkUpdateStudentGroupOverrideDates,
  getStudentGroup,
  getStudentGroups,
} from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import type { StudentGroupWithScheduleAndLevel, UpdateStudentGroupBulkActionsResults } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { ReactNode, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

const overrideDatesActionOptions = [
  { value: 'update', label: 'Update override dates' },
  { value: 'remove', label: 'Remove override dates' },
] as const;

const overrideDatesFormSchema = z
  .object({
    action: z.enum(['update', 'remove']),
    overrideStartPlainDate: z.instanceof(Temporal.PlainDate).optional(),
    overrideEndPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.action !== 'update') {
      return;
    }
    if (!data.overrideStartPlainDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Override start date is required',
        path: ['overrideStartPlainDate'],
      });
    }
    if (!data.overrideEndPlainDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Override end date is required',
        path: ['overrideEndPlainDate'],
      });
    }
    if (
      data.overrideStartPlainDate &&
      data.overrideEndPlainDate &&
      Temporal.PlainDate.compare(data.overrideStartPlainDate, data.overrideEndPlainDate) > 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Start date must be on or before the end date',
        path: ['overrideEndPlainDate'],
      });
    }
  });

type OverrideDatesFormValues = z.infer<typeof overrideDatesFormSchema>;

function selectedClassesDescription(count: number): string {
  return `Apply changes to ${count} selected ${count === 1 ? 'class' : 'classes'}`;
}

/** Prefill a date only when every selected class currently has that same override date. */
function sharedOverridePlainDate(dates: Array<Temporal.PlainDate | null | undefined>): Temporal.PlainDate | undefined {
  const first = dates[0];
  if (!first) {
    return undefined;
  }
  const allSame = dates.every((date) => date != null && Temporal.PlainDate.compare(date, first) === 0);
  return allSame ? first : undefined;
}

/** Expects `formData` already validated by `overrideDatesFormSchema`. */
function saveOverrideDates(
  studentGroupIds: string[],
  { action, overrideStartPlainDate, overrideEndPlainDate }: OverrideDatesFormValues,
): Promise<UpdateStudentGroupBulkActionsResults> {
  if (action === 'remove') {
    return bulkRemoveStudentGroupOverrideDates({ studentGroupIds });
  }
  if (!overrideStartPlainDate || !overrideEndPlainDate) {
    return Promise.reject(new Error('Override start and end dates are required'));
  }
  return bulkUpdateStudentGroupOverrideDates({ studentGroupIds, overrideStartPlainDate, overrideEndPlainDate });
}

interface BulkUpdateOverrideDatesProps {
  studentGroups: StudentGroupWithScheduleAndLevel[];
  trigger: ReactNode;
  onSuccess?: () => void;
}

export const BulkUpdateOverrideDates = ({ studentGroups, trigger, onSuccess }: BulkUpdateOverrideDatesProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const studentGroupIds = studentGroups.map((group) => group.id);
  const { mutateAsync } = useMutation({
    mutationFn: (formData: OverrideDatesFormValues) => saveOverrideDates(studentGroupIds, formData),
  });

  const defaultValues = useMemo<OverrideDatesFormValues>(
    () => ({
      action: 'update',
      overrideStartPlainDate: sharedOverridePlainDate(studentGroups.map((group) => group.overrideStartPlainDate)),
      overrideEndPlainDate: sharedOverridePlainDate(studentGroups.map((group) => group.overrideEndPlainDate)),
    }),
    [studentGroups],
  );

  const onSubmit = async ({ formData, closeModal }: SubmitProps<OverrideDatesFormValues>) => {
    try {
      const { success, errors } = await mutateAsync(formData);
      if (success > 0) {
        toast({ title: `${success} classes updated` });
      }
      if (errors && errors.length > 0) {
        toast({
          title: errors.length === 1 ? 'Error' : 'Errors',
          description: (
            <ul>
              {errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          ),
        });
      }
      if (success === 0 && (!errors || errors.length === 0)) {
        toast({ title: 'No classes were updated' });
      }

      queryClient.invalidateQueries(getStudentGroups());
      queryClient.invalidateQueries({ queryKey: ['AVAILABLE_COACHES'] });
      for (const id of studentGroupIds) {
        queryClient.invalidateQueries(getStudentGroup(id));
      }

      closeModal();
      onSuccess?.();
    } catch (error: unknown) {
      toast({
        title: airServerErrorFrom(error)?.userFacingMessage || 'Failed to update classes',
        variant: 'destructive',
      });
    }
  };

  return (
    <FormModal
      modalTitle="Update override dates"
      modalDescription={selectedClassesDescription(studentGroups.length)}
      formSchema={overrideDatesFormSchema}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      trigger={trigger}
      dataTestId="classes-modal-update-override-dates"
      validateOnOpen
      resetOnDefaultValuesChange
      dialogContentProps={{ className: 'max-w-2xl lg:max-w-2xl' }}
      hideConfirmButton
      footerContent={({ form }) => <OverrideDatesSubmitButton form={form} />}
    >
      {({ form }) => <OverrideDatesFields form={form} />}
    </FormModal>
  );
};

function OverrideDatesSubmitButton({ form }: { form: UseFormReturn<OverrideDatesFormValues> }) {
  const action = form.watch('action');
  return (
    <Button
      type="submit"
      loading={form.formState.isSubmitting}
      disabled={!form.formState.isValid || form.formState.isSubmitting}
      data-testid="classes-modal-update-override-dates-button-submit"
    >
      {action === 'remove' ? 'Remove dates' : 'Update dates'}
    </Button>
  );
}

function OverrideDatesFields({ form }: { form: UseFormReturn<OverrideDatesFormValues> }) {
  const action = form.watch('action');
  return (
    <div className="flex flex-col gap-4 py-2">
      <FormField
        control={form.control}
        name="action"
        render={({ field }) => (
          <FormItem>
            <p id="classes-override-dates-action-label" className="text-sm font-bold leading-none">
              Choose an action
            </p>
            <FormControl>
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="flex flex-row flex-wrap gap-6"
                aria-labelledby="classes-override-dates-action-label"
                aria-describedby="classes-override-dates-helper"
              >
                {overrideDatesActionOptions.map((option) => {
                  const id = `classes-override-dates-action-${option.value}`;
                  return (
                    <div key={option.value} className="flex items-center gap-2">
                      <RadioGroupItem
                        id={id}
                        value={option.value}
                        data-testid={`classes-modal-update-override-dates-radio-${option.value}`}
                      />
                      <FormLabel htmlFor={id} className="font-normal">
                        {option.label}
                      </FormLabel>
                    </div>
                  );
                })}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {action === 'update' ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormInputDate
            form={form}
            name="overrideStartPlainDate"
            label="Manually override start date"
            className="min-w-0"
            triggerButtonProps={{
              className: 'w-full',
              'data-testid': 'classes-modal-update-override-dates-input-start',
            }}
          />
          <FormInputDate
            form={form}
            name="overrideEndPlainDate"
            label="Manually override end date"
            className="min-w-0"
            triggerButtonProps={{
              className: 'w-full',
              'data-testid': 'classes-modal-update-override-dates-input-end',
            }}
          />
        </div>
      ) : null}
      <div id="classes-override-dates-helper" className="flex items-start gap-2 text-sm text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <p>
          {action === 'remove'
            ? "Removing overrides makes each selected class use its assigned schedule's dates."
            : 'This action does not generate, recreate, remove, or reschedule existing sessions. Admins must use the update session actions when session changes are needed.'}
        </p>
      </div>
    </div>
  );
}
